<script>
import Tooltip from '@/components/widgets/errors/validations-tooltip.vue';
import { required, minLength } from 'vuelidate/lib/validators';
import Repositories from '@/repositories';
const StepsRepository = Repositories.get('steps');
const CategoriesRepository = Repositories.get('categories');
import Multiselect from 'vue-multiselect';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import HeaderContentLayout from '@/router/layouts/header-content-layout';
import SharedUrlButton from '@/components/widgets/buttons/shared-url';

export default {
  page: {
    title: 'Categories',
  },
  components: {
    Loading,
    Multiselect,
    Tooltip,
    HeaderContentLayout,
    SharedUrlButton,
  },
  data() {
    return {
      state: this.$route.name == 'editCategory' ? 'edit' : 'create',
      isLoading: false,
      category: null,
      steps: [],
    };
  },
  async created() {
    await this.loadData();
  },
  methods: {
    async loadData() {
      try {
        this.isLoading = true;

        let stepsPromise = StepsRepository.getAll();

        let promises = [stepsPromise];

        if (this.state == 'edit') {
          let categoryPromise = CategoriesRepository.get(
            this.$route.params.idPublic
          );

          promises.push(categoryPromise);
        } else {
          this.category = {
            id: 0,
            idPublic: null,
            name: '',
            leadTime: 0,
            referenceDate: null,
            consumptionStep: null,
            categoryType: null,
            sharedUrlKey: null,
          };
        }

        const [steps, category] = await Promise.all(promises);

        await this.loadSteps(steps);

        if (this.state == 'edit') {
          await this.loadCategory(category);
        }
      } catch {
        this.$toast.errorToast(this.$t('errors.errorLoading'));
      } finally {
        this.isLoading = false;
      }
    },
    async loadSteps(stepsResponse) {
      this.steps = stepsResponse.data.data.sort(function (a, b) {
        return a.generalOrder - b.generalOrder;
      });
    },
    async loadCategory(categoryResponse) {
      if (categoryResponse == null) {
        this.category = null;
        return;
      }

      let category = categoryResponse.data;

      category.categoryType = this.categoryTypes.find(
        (x) => x.idPublic == category.categoryTypeIdPublic
      );

      category.consumptionStep = this.steps.find(
        (x) => x.idPublic == category.consumptionStepIdPublic
      );

      category.referenceDate = this.referenceDates.find(
        (x) => x.value == category.referenceDate
      );

      this.category = category;
    },
    async onSubmit() {
      this.$v.category.$touch();
      if (!this.$v.category.$invalid) {
        await this.createOrUpdate();
      }
    },
    async createOrUpdate() {
      try {
        this.isLoading = true;

        let apiWrapper = {
          id: 0,
          idPublic: null,
          name: this.category.name,
          leadTime: this.category.leadTime,
          referenceDate: this.category.referenceDate?.value,
          consumptionStepIdPublic: this.category.consumptionStep?.idPublic,
          categoryTypeIdPublic: this.category.categoryType?.idPublic,
          sharedUrlKey: this.category.sharedUrlKey,
        };

        if (this.$route.name == 'editCategory') {
          // Update
          apiWrapper.id = this.category.id;
          apiWrapper.idPublic = this.$route.params.idPublic;

          await CategoriesRepository.update(
            apiWrapper,
            this.$route.params.idPublic
          );
        } else {
          // Create
          await CategoriesRepository.create(apiWrapper);
        }

        this.$toast.successToast(this.$t('general.success'));
        this.$router.push({ name: 'categoriesConfig' });
      } catch {
        this.$toast.errorToast(this.$t('errors.errorLoading'));
      } finally {
        this.isLoading = false;
      }
    },
    cancel() {
      this.$router.push({ name: 'categoriesConfig' });
    },
    sharedUrlKeyUpdated(sharedUrlKey) {
      this.category.sharedUrlKey = sharedUrlKey;
    },
  },
  computed: {
    categoryTypes() {
      return this.$store.state.cache.categoryTypes;
    },
    sharedUrl() {
      if (
        this.category == null ||
        this.category.id == null ||
        this.category.id <= 0
      )
        return null;

      return (
        process.env.VUE_APP_REDIRECT_URL +
        'shared/' +
        this.$store.state.user.config.organisationCode +
        '/consumption/categories/' +
        this.category.id
      );
    },
    sharedUrlRepo() {
      return CategoriesRepository;
    },
    referenceDates() {
      return [
        {
          name: this.$t('general.installationDate'),
          value: 'installationDate',
        },
        {
          name: this.$t('general.deliveryDate'),
          value: 'deliveryDate',
        },
        {
          name: this.$t('general.productionDate'),
          value: 'productionDate',
        },
      ];
    },
  },
  validations() {
    return {
      category: {
        name: { required, minLength: minLength(1) },
        categoryType: { required },
      },
    };
  },
};
</script>

<template>
  <HeaderContentLayout
    @ctrl-enter-pressed="onSubmit()"
    :title="
      $route.name == 'editCategory' ? $t('general.edit') : $t('general.create')
    "
    :overflow="false"
  >
    <template v-slot:header>
      <div class="flex-box">
        <b-button
          type="submit"
          variant="success"
          class="mr-2"
          @click="onSubmit"
        >
          {{
            $route.name == 'editCategory'
              ? $t('general.save')
              : $t('general.create')
          }}
        </b-button>
        <b-button
          @click="cancel()"
          tabindex="-1"
          type="button"
          variant="secondary"
        >
          {{ $t('general.cancel') }}
        </b-button>
        <div class="flex-push">
          <SharedUrlButton
            v-if="sharedUrl != null"
            :repository="sharedUrlRepo"
            :objectId="category.id"
            target="categorySharedLink"
            :baseSharedUrl="sharedUrl"
            :baseSharedKey="category.sharedUrlKey"
            @shared-url-key-updated="sharedUrlKeyUpdated"
            :title="category.name"
          />
        </div>
      </div>
    </template>

    <loading
      :active.sync="isLoading"
      :can-cancel="false"
      color="#f1b44c"
      :width="70"
      :height="70"
      loader="dots"
      :is-full-page="false"
    >
    </loading>

    <b-form v-if="category != null" style="max-width: 1000px">
      <b-row>
        <b-col>
          <b-form-group
            id="input-group-name"
            :label="$t('general.name')"
            label-for="input-name"
          >
            <b-form-input
              id="input-name"
              :class="{
                'is-invalid': $v.category.name.$error,
              }"
              v-model="category.name"
              type="text"
              :placeholder="$t('general.name')"
            ></b-form-input>
            <Tooltip :validations="$v.category.name" />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col>
          <b-form-group
            id="group-category-types"
            :label="$t('categories.categoryTypes')"
            label-for="categoryTypeInput"
          >
            <multiselect
              id="categoryTypeInput"
              :class="{
                'is-invalid': $v.category.categoryType.$error,
              }"
              v-model="category.categoryType"
              :options="categoryTypes"
              :show-labels="false"
              :searchable="true"
              :label="'name'"
              track-by="idPublic"
              :placeholder="$t('general.pickAValue')"
            >
              <template v-slot:noOptions>
                {{ $t('general.noResult') }}
              </template>
            </multiselect>
            <Tooltip :validations="$v.category.categoryType" />
          </b-form-group>
        </b-col>
        <b-col>
          <b-form-group
            id="group-consumption-steps"
            :label="$t('categories.consumptionStep')"
            label-for="consumptionStepInput"
          >
            <multiselect
              id="consumptionStepInput"
              v-model="category.consumptionStep"
              :options="steps"
              :show-labels="false"
              :searchable="true"
              :label="'name'"
              track-by="idPublic"
              :placeholder="$t('general.pickAValue')"
            >
              <template v-slot:noOptions>
                {{ $t('general.noResult') }}
              </template>
            </multiselect>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col>
          <b-form-group
            id="group-lead-time"
            :label="$t('general.leadTime')"
            label-for="leadTimeInput"
          >
            <b-form-input type="number" v-model="category.leadTime" />
          </b-form-group>
        </b-col>
        <b-col>
          <b-form-group
            id="group-reference-date"
            :label="$t('general.referenceDate')"
            label-for="referenceDateInput"
          >
            <multiselect
              id="referenceDateInput"
              v-model="category.referenceDate"
              :options="referenceDates"
              :show-labels="false"
              :searchable="true"
              :label="'name'"
              track-by="value"
              :placeholder="$t('general.pickAValue')"
            >
              <template v-slot:noOptions>
                {{ $t('general.noResult') }}
              </template>
            </multiselect>
          </b-form-group>
        </b-col>
      </b-row>
    </b-form>
  </HeaderContentLayout>
</template>

<style scoped>
.group-box {
  border-radius: 5px;
  min-height: 200px;
  overflow-x: hidden;
  overflow-y: hidden;
  padding: 15px 20px;
  margin-bottom: 16px;
  background-color: #f5f5f5;
}
</style>